/**
 * AdminStudioProjects — `/admin/studio`
 * -------------------------------------
 * Rich admin view of every Studio project (prospect) created via the
 * MetaConnect wizard. Provides:
 *   - Aggregate stats (total, published, drafts, avg progress, last 24 h)
 *   - Sortable / filterable table (status, service, search)
 *   - Row click → detail drawer with brand + services + answers per module
 *   - CSV export of the current filtered set
 *
 * API endpoints:
 *   GET  /api/admin/studio/projects          (list + stats)
 *   GET  /api/admin/studio/projects/{id}     (full detail)
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Search, Download, X, Clock, Users,
  Mail as MailIcon, Phone, Building2, ExternalLink, FileText, CheckCircle2,
  Timer,
} from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Labels for wizard steps (aligned with STEPS in Studio.jsx). "other" catches
// heartbeats coming from any non-wizard page (landing, blog, sandbox, …).
const STEP_LABELS = {
  "0": "Étape 1 · Services",
  "1": "Étape 2 · Structure",
  "2": "Étape 3 · Identité",
  "3": "Étape 4 · Savoir-faire",
  "4": "Étape 5 · Configuration",
  "5": "Étape 6 · Récapitulatif",
  "sandbox:dashboard": "Sandbox · Tableau de bord",
  "sandbox:telesoin-quota": "Sandbox · Quota télésoin",
  "sandbox:facturation-pro": "Sandbox · Facturation Factur-X",
  "sandbox:messagerie-instant": "Sandbox · Messagerie instantanée",
  "sandbox:reseau": "Sandbox · Réseau de proximité",
  "sandbox:telecons": "Sandbox · Téléconsultation",
  "sandbox:agenda": "Sandbox · Calendrier",
  "other": "Hors wizard (landing, sandbox…)",
};

function fmtDuration(sec) {
  const s = Number(sec) || 0;
  if (s < 1) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// Normalize a service item to a printable string. In older Studio payloads,
// services is an array of strings (["Téléconsultation", ...]); newer payloads
// store [{id: "teleconsult", selected: true, ...}, ...].
function svcLabel(s) {
  if (!s) return "";
  if (typeof s === "string") return s;
  return s.label || s.name || s.title || s.id || "";
}

// --------------------- helpers ---------------------
function fmtAge(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} j`;
  return `${Math.floor(d / 30)} mois`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusPill({ status }) {
  const map = {
    published: { bg: "#DCFCE7", tx: "#166534", bd: "#86EFAC", label: "Publié" },
    draft:     { bg: "#FEF3C7", tx: "#92400E", bd: "#FCD34D", label: "Brouillon" },
  };
  const s = map[status] || map.draft;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold border"
          style={{ background: s.bg, color: s.tx, borderColor: s.bd }}
          data-testid={`status-${status}`}>
      {s.label}
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 shadow-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#4FC3F7]" />}
      </div>
      <div className="mt-1.5 text-[26px] font-bold text-[#0F2B5B] leading-none tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-[11.5px] text-slate-500">{sub}</div>}
    </div>
  );
}

// --------------------- Detail drawer ---------------------
function ProjectDrawer({ projectId, onClose }) {
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/studio/projects/${projectId}`)
      .then((r) => { if (!cancelled) setProj(r.data); })
      .catch(() => toast.error("Impossible de charger le projet."))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100]" data-testid="studio-project-drawer">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Projet Studio</div>
            <div className="text-[16px] font-bold text-[#0F2B5B] mt-0.5">{proj?.brand?.name || "Chargement…"}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && <div className="p-6 text-[13px] text-slate-500">Chargement du projet…</div>}

        {!loading && proj && (
          <div className="p-6 space-y-6">
            {/* Contact */}
            <section>
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">Contact prospect</div>
              <dl className="grid grid-cols-2 gap-3 text-[13px]">
                <div><dt className="text-[11px] text-slate-500">Nom</dt><dd className="font-semibold text-[#0F2B5B]">{proj.brand?.contactName || "—"}</dd></div>
                <div><dt className="text-[11px] text-slate-500">Email</dt><dd className="font-mono text-[12px] text-slate-800 break-all">{proj.brand?.contactEmail || "—"}</dd></div>
                <div><dt className="text-[11px] text-slate-500">Téléphone</dt><dd className="text-slate-800">{proj.brand?.contactPhone || "—"}</dd></div>
                <div><dt className="text-[11px] text-slate-500">Structure</dt><dd className="text-slate-800">{proj.brand?.name || "—"}</dd></div>
                <div><dt className="text-[11px] text-slate-500">RDV url</dt><dd>{proj.brand?.rdvUrl ? <a href={proj.brand.rdvUrl} target="_blank" rel="noreferrer" className="text-[#0EA5E9] hover:underline break-all">{proj.brand.rdvUrl}</a> : "—"}</dd></div>
                <div><dt className="text-[11px] text-slate-500">IP</dt><dd className="font-mono text-[11.5px] text-slate-600">{proj.ip || "—"}</dd></div>
              </dl>
            </section>

            {/* Services */}
            <section>
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">Services sélectionnés ({(proj.services || []).length})</div>
              <div className="flex flex-wrap gap-1.5">
                {(proj.services || []).map((s, i) => {
                  const label = svcLabel(s);
                  return label ? <span key={i} className="text-[11.5px] px-2.5 py-1 rounded-full bg-[#EFF6FF] text-[#0F2B5B] border border-[#BAE6FD] font-medium">{label}</span> : null;
                })}
                {(!proj.services || proj.services.length === 0) && <span className="text-[12px] italic text-slate-400">Aucun service sélectionné.</span>}
              </div>
            </section>

            {/* Timestamps */}
            <section>
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">Chronologie</div>
              <dl className="grid grid-cols-2 gap-3 text-[13px]">
                <div><dt className="text-[11px] text-slate-500">Créé le</dt><dd className="text-slate-800">{fmtDate(proj.created_at)}</dd></div>
                <div><dt className="text-[11px] text-slate-500">Modifié le</dt><dd className="text-slate-800">{fmtDate(proj.updated_at)}</dd></div>
                <div><dt className="text-[11px] text-slate-500">Publié le</dt><dd className="text-slate-800">{fmtDate(proj.published_at)}</dd></div>
                <div><dt className="text-[11px] text-slate-500">Email envoyé</dt><dd className={proj.email_sent ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{proj.email_sent ? "Oui" : "Non"}</dd></div>
              </dl>
              {proj.email_error && <div className="mt-2 text-[11.5px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">Erreur email : {proj.email_error}</div>}
              {proj.pdf_error && <div className="mt-2 text-[11.5px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">Erreur PDF : {proj.pdf_error}</div>}
            </section>

            {/* Engagement time (from anonymous heartbeat tracker) */}
            {proj.analytics && (
              <section data-testid="drawer-engagement">
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                  <Timer className="w-3 h-3 text-[#4FC3F7]" /> Temps passé sur l&apos;application
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="rounded-lg bg-[#F0F9FF] border border-[#BAE6FD] px-3 py-2.5">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Total</div>
                    <div className="text-[18px] font-bold text-[#0F2B5B] tabular-nums">{fmtDuration(proj.analytics.total_seconds)}</div>
                  </div>
                  <div className="rounded-lg bg-[#F0F9FF] border border-[#BAE6FD] px-3 py-2.5">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Sessions</div>
                    <div className="text-[18px] font-bold text-[#0F2B5B] tabular-nums">{proj.analytics.session_count || 0}</div>
                  </div>
                  <div className="rounded-lg bg-[#F0F9FF] border border-[#BAE6FD] px-3 py-2.5">
                    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Dernière visite</div>
                    <div className="text-[13px] font-semibold text-[#0F2B5B]">{proj.analytics.last_seen ? fmtDate(proj.analytics.last_seen) : "—"}</div>
                  </div>
                </div>

                {/* Time by wizard step */}
                {proj.analytics.time_by_step && Object.keys(proj.analytics.time_by_step).length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Répartition par étape du wizard</div>
                    <div className="space-y-1.5">
                      {(() => {
                        const entries = Object.entries(proj.analytics.time_by_step);
                        const maxSec = Math.max(1, ...entries.map(([, v]) => v));
                        // Sort: numeric step first, "other" last.
                        entries.sort(([a], [b]) => {
                          if (a === "other") return 1;
                          if (b === "other") return -1;
                          return Number(a) - Number(b);
                        });
                        return entries.map(([step, sec]) => (
                          <div key={step} className="flex items-center gap-2 text-[12px]">
                            <div className="w-[220px] text-slate-700 shrink-0">{STEP_LABELS[step] || (String(step).startsWith("sandbox:") ? `Sandbox · ${String(step).slice(8)}` : `Étape ${step}`)}</div>
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${(sec / maxSec) * 100}%`, background: step === "other" ? "#94A3B8" : "#4FC3F7" }} />
                            </div>
                            <div className="w-[80px] text-right font-semibold text-[#0F2B5B] tabular-nums">{fmtDuration(sec)}</div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* Recent sessions list */}
                {proj.sessions && proj.sessions.length > 0 && (
                  <details className="mt-3 rounded-lg border border-slate-200 bg-slate-50">
                    <summary className="cursor-pointer px-3 py-2 text-[12px] font-semibold text-[#0F2B5B]">
                      20 dernières sessions
                    </summary>
                    <div className="px-3 pb-3 pt-1 space-y-1">
                      {proj.sessions.map((s) => (
                        <div key={s.id} className="text-[11.5px] text-slate-700 flex items-center justify-between gap-2 border-b border-slate-200/60 pb-1 last:border-0">
                          <div className="min-w-0 truncate">
                            <span className="font-mono text-slate-500">{fmtDate(s.started_at)}</span>
                            <span className="mx-1.5 text-slate-400">·</span>
                            <span>{s.page_path}</span>
                            {s.wizard_step != null && (
                              <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-[#EFF6FF] text-[#0F2B5B] border border-[#BAE6FD]">
                                {typeof s.wizard_step === "string" ? (STEP_LABELS[s.wizard_step] || s.wizard_step) : `Étape ${Number(s.wizard_step) + 1}`}
                              </span>
                            )}
                          </div>
                          <div className="tabular-nums font-semibold text-[#0F2B5B]">{fmtDuration(s.duration_seconds)}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </section>
            )}

            {/* Answers per module (if available) */}
            {proj.sections && (
              <section>
                <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">Réponses aux 8 modules</div>
                <div className="space-y-3">
                  {Object.entries(proj.sections).map(([mod, mdata]) => (
                    <details key={mod} className="rounded-lg border border-slate-200 bg-slate-50">
                      <summary className="cursor-pointer px-3 py-2 text-[13px] font-semibold text-[#0F2B5B] flex items-center justify-between">
                        <span>{mod} — {mdata?.title || ""}</span>
                        <span className="text-[11px] text-slate-500 font-normal">{Object.keys(mdata?.answers || mdata || {}).length} question(s)</span>
                      </summary>
                      <div className="px-3 pb-3 pt-1 space-y-1.5 text-[12.5px]">
                        {Object.entries(mdata?.answers || mdata || {}).map(([q, a]) => (
                          <div key={q} className="rounded bg-white border border-slate-100 px-2.5 py-1.5">
                            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{q}</div>
                            <div className="text-slate-800">{Array.isArray(a) ? a.join(" · ") : (typeof a === "object" ? JSON.stringify(a) : String(a || "—"))}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}
                  {Object.keys(proj.sections).length === 0 && <div className="text-[12px] italic text-slate-400">Aucune réponse enregistrée.</div>}
                </div>
              </section>
            )}

            {/* Preview link */}
            <section className="pt-4 border-t border-slate-200">
              <a
                href={`/studio/preview/${proj.id}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white px-4 py-2 rounded-md"
                style={{ background: "#0F2B5B" }}
                data-testid="drawer-preview-link"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Ouvrir l&apos;aperçu sandbox
              </a>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

// --------------------- Main page ---------------------
export default function AdminStudioProjects() {
  const [data, setData] = useState({ projects: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/studio/projects`)
      .then((r) => setData(r.data || { projects: [], stats: null }))
      .catch(() => toast.error("Impossible de charger la liste des projets."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data.projects || []).filter((p) => {
      if (status !== "all" && p.status !== status) return false;
      if (!term) return true;
      const hay = [p.brand_name, p.contact_name, p.contact_email, p.contact_phone, ...(p.services || []).map(svcLabel)]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [data.projects, q, status]);

  const exportCsv = () => {
    const header = ["ID", "Créé", "Statut", "Progression %", "Structure", "Contact", "Email", "Téléphone", "Services", "Email envoyé", "IP", "Temps total (s)", "Sessions", "Dernière visite"];
    const rows = filtered.map((p) => [
      p.id, p.created_at, p.status, p.progress_pct,
      p.brand_name, p.contact_name, p.contact_email, p.contact_phone,
      (p.services || []).map(svcLabel).filter(Boolean).join("|"), p.email_sent ? "oui" : "non", p.ip,
      p.total_seconds || 0, p.session_count || 0, p.last_seen || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `studio-projets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`${filtered.length} projet(s) exporté(s).`);
  };

  const stats = data.stats || {};

  return (
    <AdminPageShell
      title="Projets Studio"
      subtitle="Explorez tous les prospects issus du wizard MetaConnect Studio"
      tab="studio"
      seoTitle="Admin · Projets Studio"
      seoCanonical="/admin/studio"
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="studio-admin-stats">
        <StatCard label="Total projets" value={stats.total ?? "—"} sub="depuis le début" icon={Sparkles} testid="stat-total" />
        <StatCard label="Publiés" value={stats.published ?? "—"} sub="CDC générés" icon={CheckCircle2} testid="stat-published" />
        <StatCard label="Brouillons" value={stats.drafts ?? "—"} sub="en cours de configuration" icon={FileText} testid="stat-drafts" />
        <StatCard label="Progression moy." value={`${stats.avg_progress ?? 0}%`} sub="questions répondues" icon={Users} testid="stat-progress" />
        <StatCard label="Dernières 24 h" value={stats.last_24h ?? "—"} sub="nouveaux prospects" icon={Clock} testid="stat-24h" />
        <StatCard label="Temps moyen" value={fmtDuration(stats.avg_seconds || 0)} sub={`Total : ${fmtDuration(stats.total_seconds || 0)}`} icon={Timer} testid="stat-time" />
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (structure, contact, email, service…)"
            data-testid="studio-admin-search"
            className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-[13px] bg-white text-slate-900"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="studio-admin-status-filter"
          className="border border-slate-300 rounded-md px-3 py-2 text-[13px] bg-white text-slate-900"
        >
          <option value="all">Tous statuts</option>
          <option value="published">Publiés</option>
          <option value="draft">Brouillons</option>
        </select>
        <button
          onClick={load}
          disabled={loading}
          data-testid="studio-admin-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </button>
        <button
          onClick={exportCsv}
          disabled={!filtered.length}
          data-testid="studio-admin-export"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold text-white disabled:opacity-40"
          style={{ background: "#0F2B5B" }}
        >
          <Download className="w-3.5 h-3.5" /> Exporter CSV ({filtered.length})
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="studio-admin-table">
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 grid grid-cols-[1.4fr,1.4fr,1.4fr,0.9fr,1fr,0.9fr,0.9fr,0.7fr] gap-3 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
          <div>Structure</div>
          <div>Contact</div>
          <div>Services</div>
          <div>Progression</div>
          <div>Temps passé</div>
          <div>Statut</div>
          <div>Créé</div>
          <div className="text-right">Action</div>
        </div>
        <div className="divide-y divide-slate-100">
          {loading && <div className="px-4 py-6 text-[13px] text-slate-500 text-center">Chargement des projets…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-4 py-8 text-[13px] text-slate-500 text-center italic">
              Aucun projet correspondant à vos critères.
            </div>
          )}
          {!loading && filtered.map((p) => (
            <div key={p.id} className="grid grid-cols-[1.4fr,1.4fr,1.4fr,0.9fr,1fr,0.9fr,0.9fr,0.7fr] gap-3 px-4 py-3 items-center text-[13px] hover:bg-slate-50" data-testid={`studio-project-row-${p.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-[#0F2B5B] truncate flex items-center gap-1.5"><Building2 className="w-3 h-3 text-slate-400 shrink-0" />{p.brand_name || "—"}</div>
                <div className="text-[10.5px] text-slate-400 truncate" title={p.id}>{p.id}</div>
              </div>
              <div className="min-w-0">
                <div className="text-slate-800 truncate">{p.contact_name || "—"}</div>
                <div className="text-[11px] text-slate-500 truncate flex items-center gap-1"><MailIcon className="w-2.5 h-2.5" />{p.contact_email || "—"}</div>
                {p.contact_phone && <div className="text-[11px] text-slate-500 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{p.contact_phone}</div>}
              </div>
              <div className="flex flex-wrap gap-1">
                {(p.services || []).slice(0, 4).map((s, i) => {
                  const label = svcLabel(s);
                  return label ? <span key={i} className="text-[10.5px] px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#0F2B5B] border border-[#BAE6FD]">{label}</span> : null;
                })}
                {(p.services || []).length > 4 && <span className="text-[10.5px] text-slate-500">+{p.services.length - 4}</span>}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden max-w-[80px]">
                    <div className="h-full rounded-full" style={{ width: `${p.progress_pct || 0}%`, background: p.progress_pct >= 100 ? "#10B981" : "#4FC3F7" }} />
                  </div>
                  <span className="text-[11px] text-slate-600 tabular-nums">{p.progress_pct || 0}%</span>
                </div>
              </div>
              <div data-testid={`time-cell-${p.id}`}>
                <div className="flex items-center gap-1 text-[12.5px] font-semibold text-[#0F2B5B] tabular-nums">
                  <Timer className="w-3 h-3 text-[#4FC3F7]" />
                  {fmtDuration(p.total_seconds || 0)}
                </div>
                <div className="text-[10.5px] text-slate-500">
                  {p.session_count || 0} session{(p.session_count || 0) > 1 ? "s" : ""}
                  {p.last_seen && ` · ${fmtAge(Math.floor((Date.now() - new Date(p.last_seen).getTime()) / 60000))}`}
                </div>
              </div>
              <div><StatusPill status={p.status} /></div>
              <div className="text-[11.5px] text-slate-600">
                <div>{fmtDate(p.created_at).split(",")[0]}</div>
                <div className="text-[10.5px] text-slate-400">{fmtAge(p.age_minutes)}</div>
              </div>
              <div className="text-right">
                <button
                  onClick={() => setOpenId(p.id)}
                  data-testid={`open-project-${p.id}`}
                  className="text-[11.5px] font-semibold px-2.5 py-1 rounded-md text-white"
                  style={{ background: "#0F2B5B" }}
                >
                  Détails
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {openId && <ProjectDrawer projectId={openId} onClose={() => setOpenId(null)} />}
    </AdminPageShell>
  );
}
